import { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { loadContent } from '@redux/core/actions';
import { AnyContentMap, selectContent, selectContentLoading } from '@redux/core/reducers';
import { IState } from '@redux/reducer';
import ContentRef from '@utils/contentRef';
import { getBasicContentDetails } from '@utils/contentDetails';

const useContentRefs = (refs?: string[]): AnyContentMap => {
  const dispatch = useDispatch();

  // Serialize refs into a stable key so that the dispatch effect and selector
  // memo re-run whenever any ref value changes.
  const refsKey = refs?.map(String).join('\n') ?? '';

  const refsAsStrings: string[] = useMemo(() => (refsKey ? refsKey.split('\n') : []), [refsKey]);

  useEffect(() => {
    if (refsAsStrings.length > 0) {
      dispatch(loadContent(refsAsStrings));
    }
  }, [refsAsStrings, dispatch]);

  const contentSelector = useMemo(() => selectContent(refsAsStrings), [refsAsStrings]);
  const content = useSelector(contentSelector, shallowEqual);
  return content;
};

export const useContentRefFromUrl = (urlFragment: string) => {
  const ref = ContentRef.fromUrlFragment(urlFragment);
  const refAsString = ref.toString();
  const { [refAsString]: content } = useContentRefs([refAsString]);
  const { [refAsString]: loading } = useSelector(selectContentLoading([refAsString]));
  return [loading, content, ref];
};

export const useContentRef = (ref?: string) => {
  const contentRef = ref ? ContentRef.normaliseString(ref) : undefined;
  const { [contentRef ?? '']: content } = useContentRefs(contentRef ? [contentRef] : undefined);
  const { [contentRef ?? '']: rawLoading } = useSelector(
    selectContentLoading(contentRef ? [contentRef] : []),
  );

  // The contentLoading reducer only adds a ref to state when loadContent is dispatched,
  // so its absence means the dispatch hasn't happened yet
  const loadingInitiated = useSelector((state: IState) =>
    contentRef ? contentRef in state.core.contentLoading : false,
  );

  if (!contentRef) {
    return {
      loading: false,
      content: undefined,
      isDeleted: false,
      url: undefined,
    };
  }

  const loading = rawLoading || !loadingInitiated;

  const contentDetails = content && getBasicContentDetails(content);
  return {
    loading,
    content,
    isDeleted: loadingInitiated && !rawLoading && !content,
    url: contentDetails?.content_ref?.toDashboardUrl(contentDetails?.content_type),
  };
};

export default useContentRefs;
